import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { SubmitJourneyFormResult } from "./types";

export type SubmitJourneyFormInput = {
  progressId: string;
  formId: string;
  payload: Record<string, unknown>;
};

// POST /api/portal/journeys/progress/:progressId/form-submission
//
// Replaces /app/portal/journeys/_actions.ts::submitJourneyFormAction.
// Pessimistic — final-form submission is where server-side validation
// matters most; no optimism. On success the worker has stamped
// completing_submission_id and (for complete_on === 'submitted' or
// auto-accepted 'accepted') already flipped progress.status to 'completed'
// via the complete_jtp_by_submission RPC.
//
// Broad-invalidate portal.journeys.all so list + detail both refetch, and
// portal.tasks.all so the dashboard active-task feed + top-nav badge
// refetch on day one when Phase 7 wires them. Deals.all invalidated to
// catch downstream-deal creation in the same shape as bucket 6's hook.
//
// Per-field validation errors arrive as 400 with `details.issues` — extract
// with the bucket-6 extractValidationIssues helper at the call site.
export function useSubmitJourneyForm() {
  const qc = useQueryClient();
  return useMutation<
    SubmitJourneyFormResult,
    ApiClientError,
    SubmitJourneyFormInput
  >({
    mutationFn: ({ progressId, formId, payload }) =>
      apiFetch<SubmitJourneyFormResult>(
        `/portal/journeys/progress/${progressId}/form-submission`,
        {
          method: "POST",
          body: JSON.stringify({ formId, payload }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.portal.journeys.all });
      void qc.invalidateQueries({ queryKey: queryKeys.portal.tasks.all });
      void qc.invalidateQueries({ queryKey: queryKeys.deals.all });
    },
  });
}
